import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { Alert } from '../components/alert';
import { ConfirmationDialog } from '../components/confirmation-dialog';
import { Loader } from '../components/loader';
import { Toast, triggerSonnerToast } from '../components/toast';
// A G drawn with three fingers, as the directions its stroke passes through: across the top to the left,
// down the side, along the bottom, up into the hook, then in along the crossbar.
const GOD_MODE_G_STROKE = ['left', 'down', 'right', 'up', 'left'];
const GOD_MODE_FINGERS = 3;
// Movement shorter than this between samples is jitter, not a change of direction.
const G_DIRECTION_STEP_PX = 20;
// Both sides of the drawn shape must be at least this long, so a stray wiggle never reads as a letter.
const MIN_G_SIZE_PX = 80;
const toDirection = (dx, dy) => (Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
const isGStroke = (path) => {
    const xs = path.map((point) => point.x);
    const ys = path.map((point) => point.y);
    if (Math.max(...xs) - Math.min(...xs) < MIN_G_SIZE_PX || Math.max(...ys) - Math.min(...ys) < MIN_G_SIZE_PX)
        return false;
    const directions = [];
    let anchor = path[0];
    for (const point of path) {
        const dx = point.x - anchor.x;
        const dy = point.y - anchor.y;
        if (Math.hypot(dx, dy) < G_DIRECTION_STEP_PX)
            continue;
        const direction = toDirection(dx, dy);
        if (directions[directions.length - 1] !== direction)
            directions.push(direction);
        anchor = point;
    }
    // In order, tolerating the extra turns a hand-drawn curve picks up between them.
    let matched = 0;
    for (const direction of directions) {
        if (direction === GOD_MODE_G_STROKE[matched])
            matched += 1;
        if (matched === GOD_MODE_G_STROKE.length)
            return true;
    }
    return false;
};
const centroidOf = (touches) => {
    let x = 0;
    let y = 0;
    for (let index = 0; index < touches.length; index += 1) {
        x += touches[index].clientX;
        y += touches[index].clientY;
    }
    return { x: x / touches.length, y: y / touches.length };
};
// For godMode animation
const shakeAnimation = `
  @keyframes shake {
    0% { transform: translate(1px, 1px) rotate(0deg); }
    10% { transform: translate(-1px, -2px) rotate(-3deg); }
    20% { transform: translate(-3px, 0px) rotate(3deg); }
    30% { transform: translate(3px, 2px) rotate(0deg); }
    40% { transform: translate(1px, -1px) rotate(3deg); }
    50% { transform: translate(-1px, 2px) rotate(-3deg); }
    60% { transform: translate(-3px, 1px) rotate(0deg); }
    70% { transform: translate(3px, 1px) rotate(-3deg); }
    80% { transform: translate(-1px, -1px) rotate(3deg); }
    90% { transform: translate(1px, 2px) rotate(0deg); }
    100% { transform: translate(1px, -2px) rotate(-3deg); }
  }
`;
export const SourContext = createContext({
    application: 'admin-portal',
    isLoading: false,
    godModeActive: false,
    isDesktop: false,
    setIsLoading: () => {
        return;
    },
    setShowAlert: () => {
        return;
    },
    setShowToast: () => {
        return;
    },
    openConfirmationDialog: () => {
        return;
    },
});
const SourContextProvider = ({ children, application: passedApplication, isAppAdmin, toastPosition, }) => {
    const sequence = useRef([]);
    const application = passedApplication;
    const [isLoading, setIsLoading] = useState(false);
    const [isAlertActive, activateAlert] = useState(false);
    const [confirmationConfig, setConfirmationConfig] = useState({
        title: '',
        message: '',
        actionCallback: () => { },
    });
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [alertProps, setAlertProps] = useState({});
    const [message, setMessage] = useState(undefined);
    const [godModeAnimation, setGodModeAnimation] = useState(false);
    const [godModeActive, setGodModeActive] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window?.innerWidth >= 1024);
    const handleSetIsLoading = (opts) => {
        if (typeof opts == 'boolean') {
            setMessage(undefined);
            setIsLoading(opts);
        }
        else if (typeof opts == 'object') {
            const { isLoading, message } = opts;
            setMessage(message);
            setIsLoading(isLoading);
        }
        else {
            // no paramater, we treat it as toggle... but in typescript this should complain and never be ran.
            setMessage(undefined);
            setIsLoading(!isLoading);
        }
    };
    const setShowAlert = (opts) => {
        setAlertProps(opts);
        activateAlert(true);
    };
    const setShowToast = (opts) => {
        triggerSonnerToast(opts);
    };
    const openConfirmationDialog = ({ title, message, actionCallback, confirmText, cancelText, isDelete, icon, titleIcon, }) => {
        setShowConfirmationDialog(true);
        setConfirmationConfig({
            title,
            message,
            actionCallback,
            confirmText,
            cancelText,
            isDelete,
            icon,
            titleIcon,
        });
    };
    const resetDialog = () => {
        setShowConfirmationDialog(false);
        setConfirmationConfig({
            title: '',
            message: '',
            actionCallback: () => { },
        });
    };
    const onConfirm = () => {
        resetDialog();
        confirmationConfig.actionCallback(true);
    };
    const onDismiss = () => {
        resetDialog();
        confirmationConfig.actionCallback(false);
    };
    const activateGodMode = useCallback(() => {
        if (isAppAdmin) {
            setGodModeAnimation(true);
            setGodModeActive(true);
            setTimeout(() => {
                setGodModeAnimation(false);
            }, 2000);
        }
        else {
            document.body.style.animation = 'spin 2s linear infinite';
        }
    }, [isAppAdmin]);
    // For godMode
    useEffect(() => {
        const handleKeyDown = (event) => {
            sequence.current.push(event.key);
            if (sequence.current.length === 8 &&
                sequence.current[0] === 'ArrowUp' &&
                sequence.current[1] === 'ArrowDown' &&
                sequence.current[2] === 'ArrowUp' &&
                sequence.current[3] === 'ArrowDown' &&
                sequence.current[4] === 'a' &&
                sequence.current[5] === 'b' &&
                sequence.current[6] === 'a' &&
                sequence.current[7] === 'b') {
                activateGodMode();
                sequence.current = [];
            }
            if (sequence.current.length > 8) {
                sequence.current = [];
            }
        };
        document.body.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.removeEventListener('keydown', handleKeyDown);
        };
    }, [activateGodMode]);
    // godMode on touch screens, which have no arrow keys: draw a G with three fingers
    useEffect(() => {
        let path = null;
        const handleTouchStart = (event) => {
            path = event.touches.length === GOD_MODE_FINGERS ? [centroidOf(event.touches)] : null;
        };
        const handleTouchMove = (event) => {
            if (path && event.touches.length === GOD_MODE_FINGERS)
                path.push(centroidOf(event.touches));
        };
        // Judged the moment the first finger lifts, since the fingers never leave the glass together.
        const handleTouchEnd = (event) => {
            if (!path || event.touches.length >= GOD_MODE_FINGERS)
                return;
            const drawn = path;
            path = null;
            if (isGStroke(drawn))
                activateGodMode();
        };
        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });
        document.addEventListener('touchcancel', handleTouchEnd, { passive: true });
        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            document.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [activateGodMode]);
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window?.innerWidth >= 1024);
        };
        window?.addEventListener('resize', handleResize);
        return () => window?.removeEventListener('resize', handleResize);
    }, []);
    return (_jsxs(SourContext.Provider, { value: {
            isLoading,
            setIsLoading: handleSetIsLoading,
            setShowAlert,
            setShowToast,
            openConfirmationDialog,
            application,
            godModeActive,
            isDesktop,
        }, children: [children, _jsx(Alert, { closeAlert: () => activateAlert(false), isAlertActive: isAlertActive, alertProps: alertProps }), _jsx(Toast, { position: toastPosition }), _jsx(Loader, { isLoading: isLoading, message: message }), _jsx(ConfirmationDialog, { open: showConfirmationDialog, title: confirmationConfig.title, message: confirmationConfig.message, cancelText: confirmationConfig.cancelText, confirmText: confirmationConfig.confirmText, isDelete: confirmationConfig.isDelete, onConfirm: onConfirm, onDismiss: onDismiss, icon: confirmationConfig.icon, titleIcon: confirmationConfig.titleIcon }), godModeAnimation && (_jsx("div", { className: "fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70 text-5xl", children: _jsxs("div", { style: {
                        animation: 'shake 0.5s cubic-bezier(.36,.07,.19,.97) both',
                        animationIterationCount: '100',
                        color: '#FF4500',
                    }, children: [_jsx("style", { children: shakeAnimation }), "God Mode Activated"] }) })), godModeActive && (_jsx("div", { className: "absolute bottom-2.5 left-[70px] z-[9998] cursor-pointer rounded bg-black bg-opacity-20 px-2.5 py-1 text-[#FF4500]", onClick: () => setGodModeActive(false), children: "Deactivate God Mode" }))] }));
};
export const MemoizedSourContextProvider = React.memo(SourContextProvider);
//# sourceMappingURL=sour-context.js.map